/**
 * PURE. `resolveSpec(dna)` (creatures §B10 step 2): DNA → a fully resolved build spec — option ids per slot, slider
 * values in real units, palettes, and the §A8 compatibility flags (ear holes / pockets, wings vs cape, snail legs and
 * tail, beard vs mask, eye-stalks, eyepatch). Plus the gameplay-side helpers of §A4.2: «Скопировать облик» (F4 adopt)
 * and «Смена вида» gating (GDD C114).
 */
import type { CreatureDNA } from '../../shared';
import { PARTS, option, type PartOption } from '../../content/creature/parts';
import { SPECIES_BY_ID, speciesAt, type SpeciesDef } from '../../content/creature/species';
import { DNA_FIELDS, FIELD_BY_NAME, sliderValue } from './fields';
import { buildDNA } from './codec';
import { fieldsOf, type FieldMap } from './presets';

/** Headwear classes for the §A8 ear rule. */
const HELMETS = new Set(['greatHelm', 'kettle', 'bascinet', 'pot', 'plagueMask']);
const HOODS = new Set(['cowl', 'coif', 'execHood']);
const BEARD_HIDERS = new Set(['plagueMask', 'greatHelm']);

export interface CreatureSpec {
  readonly dna: CreatureDNA;
  readonly body: SpeciesDef;
  readonly head: SpeciesDef;
  /** Enum option id per DNA field name (`o.skull === 'human'`). */
  readonly o: Readonly<Record<string, string>>;
  /** Slider values in real units per field name (`v.headH === 0.31`). */
  readonly v: Readonly<Record<string, number>>;
  /** Raw integers of every field (palettes, dyes, tinctures, bools). */
  readonly f: Readonly<FieldMap>;
  /** Digit count 3–5. */
  readonly digits: number;
  readonly flags: SpecFlags;
  /** Warnings (unknown enum index from a newer version → index 0; creatures §A8). */
  readonly warnings: readonly string[];
}

export interface SpecFlags {
  /** Helmets / pots / masks: ears pass through holes. */
  earHoles: boolean;
  /** Hoods / coifs / cowls: cloth ear pockets follow the ear bones. */
  earPockets: boolean;
  /** Horns pass through every headwear. */
  hornHoles: boolean;
  /** Snail body: legs hidden under the foot-skirt, shell instead of a tail, no footwear. */
  slugBody: boolean;
  /** Snail head: eyes on stalk tips (ear bones), brows hidden. */
  eyeStalks: boolean;
  /** Wing-arms: no sleeves, feather fingers. */
  wingArms: boolean;
  /** Toad: no tail at all. */
  noTail: boolean;
  beardHidden: boolean;
  mustacheHidden: boolean;
  /** Long cape chosen with wings: wings dropped (creatures §A8). */
  wingsDropped: boolean;
  /** Eyepatch: left eye lid shut, no iris. */
  eyepatch: boolean;
  /** Tail present (not toad, not snail). */
  hasTail: boolean;
  /** Headwear class. */
  hatKind: 'none' | 'helmet' | 'hood' | 'hat' | 'halo';
}

/** DNA → resolved spec. Never throws: bad values fall back to index 0 / neutral with a warning. */
export function resolveSpec(dna: CreatureDNA): CreatureSpec {
  const m = fieldsOf(dna);
  const warnings: string[] = [];
  const o: Record<string, string> = {};
  const v: Record<string, number> = {};
  for (const f of DNA_FIELDS) {
    if (f.kind === 'enum' && f.slot && f.slot in PARTS && f.slot !== 'species') {
      const list = (PARTS as Record<string, readonly PartOption[]>)[f.slot];
      if (m[f.name] >= list.length) { warnings.push(`${f.name}: unknown index ${m[f.name]} → 0`); m[f.name] = 0; }
      o[f.name] = option(f.slot, m[f.name]).id;
    } else if (f.kind === 'slider') {
      v[f.name] = sliderValue(f, m[f.name]);
    }
  }
  const body = SPECIES_BY_ID.get(dna.bodySpecies) ?? speciesAt(0);
  const head = SPECIES_BY_ID.get(dna.headSpecies) ?? body;
  const slugBody = !!body.rig.slugBody;
  const eyeStalks = !!head.rig.eyeStalks || o.ears === 'stalks';
  const wingArms = o.arms === 'wings';
  const noTail = !!body.rig.noTail;
  const hw = o.headwear;
  const hatKind: SpecFlags['hatKind'] = hw === 'none' ? 'none' : hw === 'haloDisc' ? 'halo' : HELMETS.has(hw) ? 'helmet' : HOODS.has(hw) ? 'hood' : 'hat';
  let wingsDropped = false;
  if (o.wings !== 'none' && m.cape > 1) { wingsDropped = true; o.wings = 'none'; m.wings = 0; }
  if (slugBody) { o.footwear = 'none'; m.footwear = 0; o.feet = 'snail'; }
  if (eyeStalks) { o.brows = 'none'; o.ears = 'stalks'; }
  const flags: SpecFlags = {
    earHoles: hatKind === 'helmet', earPockets: hatKind === 'hood', hornHoles: true, slugBody, eyeStalks, wingArms, noTail,
    beardHidden: BEARD_HIDERS.has(hw), mustacheHidden: BEARD_HIDERS.has(hw), wingsDropped,
    eyepatch: o.neckAcc === 'eyepatch', hasTail: !noTail && !slugBody, hatKind,
  };
  return { dna, body, head, o, v, f: m, digits: 3 + Math.min(2, m.digits), flags, warnings };
}

/** Slider value of a field map (helper for code that edits fields). */
export const sliderOf = (m: FieldMap, name: string): number => sliderValue(FIELD_BY_NAME.get(name)!, m[name]);

/**
 * «Скопировать облик» (creatures §A4.2 F4): every field of `foreign` except the two species; parts not native to the
 * own species and not owned are swapped to the nearest owned option of the same slot (lowest index distance, same tags).
 */
export function adoptLook(own: CreatureDNA, foreign: CreatureDNA, owned: ReadonlySet<string>): CreatureDNA {
  const fm = fieldsOf(foreign);
  const om = fieldsOf(own);
  const body = speciesAt(om.bodySpecies);
  const nativeIds = new Set<string>();
  for (const p of [body.defaults, ...body.looks.map((l) => l.preset)]) for (const [k, val] of Object.entries(p)) if (typeof val === 'string') nativeIds.add(`${k}=${val}`);
  const out: FieldMap = { ...fm, bodySpecies: om.bodySpecies, headSpecies: om.headSpecies, look: om.look };
  for (const f of DNA_FIELDS) {
    if (f.kind !== 'enum' || !f.slot || !(f.slot in PARTS) || f.slot === 'species' || f.name === 'look') continue;
    const list = (PARTS as Record<string, readonly PartOption[]>)[f.slot];
    const cur = list[out[f.name]] ?? list[0];
    const ok = (p: PartOption): boolean => p.acquisition === 'default' || owned.has(`${f.slot}.${p.id}`) || nativeIds.has(`${f.name}=${p.id}`) || p.native.includes(body.id);
    if (ok(cur)) continue;
    const tagsKey = cur.tags.join('');
    let best: PartOption | null = null;
    for (const p of list) {
      if (!ok(p)) continue;
      const score = Math.abs(p.index - cur.index) + (p.tags.join('') === tagsKey ? 0 : 0.5);
      const bestScore = best ? Math.abs(best.index - cur.index) + (best.tags.join('') === tagsKey ? 0 : 0.5) : Infinity;
      if (score < bestScore) best = p;
    }
    out[f.name] = best?.index ?? 0;
  }
  return buildDNA(out, foreign.fields.nameMode ? foreign.name : null);
}

/** Monday 04:00 (local wall ms supplied by the caller, GDD §5.4) of the «Седмица» containing `wallMs`. Pure. */
export function sedmitsaStart(wallMs: number, tzOffsetMin = 0): number {
  const DAY = 86_400_000;
  const local = wallMs - tzOffsetMin * 60_000 - 4 * 3_600_000;
  const days = Math.floor(local / DAY);
  // 1970-01-01 was a Thursday (weekday 3 with Monday = 0).
  const weekday = ((days % 7) + 3 + 7) % 7;
  return (days - weekday) * DAY + 4 * 3_600_000 + tzOffsetMin * 60_000;
}

/** «Смена вида» (GDD C114): once per Седмица in Облик, free on rebirth; never sold. */
export function canChangeSpecies(lastChangeWallMs: number | null, nowWallMs: number, opts: { rebirth?: boolean; tzOffsetMin?: number } = {}): { ok: boolean; nextAtMs: number } {
  const start = sedmitsaStart(nowWallMs, opts.tzOffsetMin);
  const next = start + 7 * 86_400_000;
  if (opts.rebirth || lastChangeWallMs === null) return { ok: true, nextAtMs: next };
  return { ok: lastChangeWallMs < start, nextAtMs: next };
}
